import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, CheckCircle2, Phone, Mail, Clock, User, MessageSquare } from "lucide-react";
import { api } from "../lib/api";
import { trackLead } from "../lib/metaPixel";
import { CONTACT } from "../data/content";

export default function LeadForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Kérjük töltse ki a nevet, e-mailt és telefonszámot.");
      return;
    }
    setLoading(true);
    try {
      const eventId =
        (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
        `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      await api.post("/leads", { ...form, event_id: eventId, event_source_url: window.location.href });
      trackLead(eventId);
      setDone(true);
      toast.success("Köszönjük! Hamarosan keressük Önt. 🔧");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Hiba történt a küldés során. Kérjük próbálja újra, vagy hívjon minket.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="arajanlat" className="relative py-24 lg:py-32 bg-slate-950 ss-grain overflow-hidden">
      <div className="absolute -top-20 left-1/4 w-[500px] h-[500px] rounded-full bg-sky-600/20 blur-[130px]" />
      <div className="relative max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
        {/* left copy */}
        <div>
          <span className="text-xs sm:text-sm uppercase tracking-widest font-bold text-sky-300 bg-white/5 px-3 py-1 rounded-full inline-block mb-4 border border-white/10">
            Árajánlatkérés
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white">
            Kérjen ingyenes árajánlatot – <span className="text-cyan-400">kötelezettség nélkül.</span>
          </h2>
          <p className="mt-4 text-lg text-slate-300 leading-relaxed">
            Töltse ki az űrlapot, és hamarosan felvesszük Önnel a kapcsolatot. A probléma leírása nem kötelező – ha nem biztos benne, telefonon is egyeztethetünk.
          </p>

          <div className="mt-8 space-y-3">
            <a href={`tel:${CONTACT.phoneHref}`} className="flex items-center gap-3 text-slate-200 hover:text-white transition-colors">
              <span className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center"><Phone className="w-5 h-5 text-cyan-400" /></span>
              <span className="font-bold">{CONTACT.phone}</span>
            </a>
            <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 text-slate-200 hover:text-white transition-colors">
              <span className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center"><Mail className="w-5 h-5 text-cyan-400" /></span>
              <span className="font-semibold">{CONTACT.email}</span>
            </a>
            <div className="flex items-center gap-3 text-slate-200">
              <span className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center"><Clock className="w-5 h-5 text-cyan-400" /></span>
              <span className="font-semibold">{CONTACT.hours}</span>
            </div>
          </div>
        </div>

        {/* form card */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="rounded-3xl bg-white p-7 lg:p-9 shadow-2xl"
        >
          {done ? (
            <div className="text-center py-12" data-testid="lead-form-success">
              <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-5">
                <CheckCircle2 className="w-9 h-9 text-emerald-600" />
              </div>
              <h3 className="font-display text-2xl font-extrabold text-slate-900">Köszönjük a megkeresést!</h3>
              <p className="mt-2 text-slate-600">Hamarosan felvesszük Önnel a kapcsolatot.</p>
              <button onClick={() => setDone(false)} className="mt-6 text-sky-700 font-bold hover:underline">
                Új árajánlat kérése
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <Field icon={User} label="Név *" testid="lead-form-name-input" value={form.name} onChange={update("name")} placeholder="Az Ön neve" />
              <Field icon={Mail} label="E-mail cím *" type="email" testid="lead-form-email-input" value={form.email} onChange={update("email")} placeholder="pelda@email.hu" />
              <Field icon={Phone} label="Telefonszám *" testid="lead-form-phone-input" value={form.phone} onChange={update("phone")} placeholder="+36 30 000 0000" />
              <div>
                <label className="block text-sm font-bold text-slate-700 mb-1.5">Probléma leírása <span className="font-normal text-slate-400">(nem kötelező)</span></label>
                <div className="relative">
                  <MessageSquare className="w-5 h-5 text-slate-400 absolute left-3.5 top-3.5" />
                  <textarea
                    data-testid="lead-form-message-input"
                    value={form.message}
                    onChange={update("message")}
                    rows={3}
                    placeholder="pl. Csöpög a csap a konyhában, vagy hideg marad a radiátor…"
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 pl-11 pr-4 py-3 text-slate-900 placeholder:text-slate-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-100 outline-none transition resize-none"
                  />
                </div>
              </div>
              <button
                type="submit"
                data-testid="lead-form-submit-button"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 bg-sky-600 hover:bg-sky-500 disabled:opacity-60 text-white font-bold py-4 rounded-xl shadow-lg shadow-sky-600/25 transition-colors"
              >
                {loading ? "Küldés…" : (<>Árajánlatot kérek <Send className="w-4 h-4" /></>)}
              </button>
              <p className="text-xs text-slate-400 text-center">Adatait kizárólag a kapcsolatfelvételhez használjuk.</p>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}

function Field({ icon: Icon, label, testid, type = "text", ...props }) {
  return (
    <div>
      <label className="block text-sm font-bold text-slate-700 mb-1.5">{label}</label>
      <div className="relative">
        <Icon className="w-5 h-5 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
        <input
          data-testid={testid}
          type={type}
          {...props}
          className="w-full rounded-xl border border-slate-200 bg-slate-50 pl-11 pr-4 py-3 text-slate-900 placeholder:text-slate-400 focus:border-sky-500 focus:ring-2 focus:ring-sky-100 outline-none transition"
        />
      </div>
    </div>
  );
}
